import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  Briefcase, 
  GraduationCap, 
  Calendar, 
  MapPin, 
  ChevronRight, 
  ChevronDown,
  CheckCircle2, 
  Sparkles,
  Layers,
  Award
} from "lucide-react";

export default function CareerTimeline() {
  const [expandedIndices, setExpandedIndices] = useState([]);

  const toggleIndex = (idx) => {
    setExpandedIndices((prev) => 
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const careerSteps = [
    {
      role: "SEO Specialist",
      company: "Seismic Software",
      logo: "/logos/seismic.webp",
      period: "Jun 2025 - Aug 2026",
      type: "Full-Time · Enterprise B2B SaaS",
      highlights: [
        "Led SEO technical execution for large-scale WordPress-to-Sitecore migration for global US SaaS platform, recovering initial 20% traffic drop in under 90 days.",
        "Engineered proprietary HEO (Hybrid Engine Optimization) framework, increasing AI-search visibility by 120% within 6 months.",
        "Organic and direct channels accounted for ~30% of company inbound leads in close alignment with the Conversion Rate Optimization (CRO) squad.",
        "Executed Seismic–Highspot domain and content consolidation, ensuring zero high-value rank losses across overlapping software categories."
      ],
      skills: ["Enterprise Migration", "Sitecore", "HEO Strategy", "AEO / GEO", "CRO Partnership", "Technical Auditing"]
    },
    {
      role: "Asst SEO Manager",
      company: "MyOperator",
      logo: "/logos/myoperator.webp",
      period: "May 2022 - May 2025",
      type: "Full-Time · B2B SaaS Platform",
      highlights: [
        "Achieved 200% organic traffic growth through data-driven GA4 and GSC analysis, identifying untapped commercial intent clusters.",
        "Organic search generated ~60% of all company inbound leads, establishing SEO as the primary pipeline generation channel.",
        "Pioneered early experimentation with LLM visibility to ensure brand inclusion in emergent AI chat search answers.",
        "Supervised and mentored a high-performing cross-functional team of 5 spanning SEO specialists, copywriters, and developers."
      ],
      skills: ["200% Organic Growth", "Team Leadership (5 FTEs)", "GA4 & GSC", "Lead Generation (60%)", "Topic Clustering"]
    },
    {
      role: "Asst Account Manager - SEO",
      company: "Work Hours",
      logo: "/logos/workhours.webp",
      period: "Jul 2020 - Apr 2022",
      type: "Full-Time · E-Learning Platform",
      highlights: [
        "Career Progression: Promoted from Digital Marketing Executive → Asst Account Manager (SEO).",
        "Achieved 35% organic traffic growth through high-intent search optimization and content auditing.",
        "Architected and executed successful email marketing automation workflows via ConvertKit, integrated with Zoho and Zendesk.",
        "Delivered hands-on SEO training and masterclasses, successfully mentoring more than 100 people on practical search strategies."
      ],
      skills: ["35% Organic Growth", "ConvertKit Automation", "100+ People Trained", "Technical SEO", "Client Account Management"]
    },
    {
      role: "SEO Expert",
      company: "Xpedient Digital Media",
      logo: "/logos/xpedient.webp",
      period: "Jan 2019 - Feb 2020",
      type: "Full-Time",
      highlights: [
        "Conducted comprehensive keyword research aligned directly with client commercial goals.",
        "Optimized content produced by writing squads, ensuring strict compliance with on-page SEO best practices.",
        "Acquired high-quality do-follow editorial backlinks through structured outreach, guest posts, and infographic syndication."
      ],
      skills: ["Keyword Research", "On-Page Optimization", "Editorial Backlinks", "Content Auditing"]
    },
    {
      role: "SEO Analyst",
      company: "Boxfinity Pvt. Ltd.",
      logo: "/logos/boxfinity.webp",
      period: "May 2018 - Nov 2018",
      type: "Full-Time",
      highlights: [
        "Promoted on-site and off-site SEO activities for high-traffic financial services websites in the US and UK.",
        "Executed multi-channel link-building campaigns through blogger outreach and high-authority web submissions."
      ],
      skills: ["International SEO (US & UK)", "Off-Page Link Building", "Financial Services SEO"]
    },
    {
      role: "Junior Digital Marketing Intern",
      company: "Brandpier",
      logo: "/logos/boomer.webp",
      period: "Sep 2017 - Feb 2018",
      type: "Internship",
      highlights: [
        "Created interactive graphics, visual infographics, and promotional videos (Biteable, Canva) for YouTube and social platforms.",
        "Promoted the Boomer Marketing App across social and community channels (Quora, Facebook, Twitter)."
      ],
      skills: ["Visual Design", "Social Engagement", "App Promotion", "Video Production"]
    }
  ];

  const allExpanded = expandedIndices.length === careerSteps.length;
  const toggleAll = () => {
    if (allExpanded) {
      setExpandedIndices([]);
    } else {
      setExpandedIndices(careerSteps.map((_, i) => i));
    }
  };

  return (
    <section id="experience" className="py-24 bg-[#080d1a] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6"
        >
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-xs font-mono uppercase tracking-widest text-cyan-400 mb-3">
              <Briefcase className="w-3.5 h-3.5 text-cyan-400" />
              Career Journey
            </div>
            <h2 className="font-heading font-black text-2xl sm:text-5xl text-white tracking-tight uppercase">
              8+ Years of <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-cyan-400 to-emerald-400">Proven Progression</span>
            </h2>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <p className="text-sm sm:text-base text-white/60 max-w-md font-medium leading-relaxed">
              From technical SEO execution and agency accounts to enterprise B2B SaaS migrations and AI search leadership.
            </p>
            <button
              onClick={toggleAll}
              className="self-start sm:self-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:border-cyan-400/40 hover:bg-cyan-500/10 text-xs font-mono text-white/70 hover:text-cyan-300 transition-all duration-150 whitespace-nowrap cursor-pointer"
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${allExpanded ? "rotate-180" : ""}`} />
              {allExpanded ? "Collapse All" : "Expand All"}
            </button>
          </div>
        </motion.div>

        {/* Timeline Container */}
        <div className="relative border-l-2 border-indigo-500/20 ml-4 sm:ml-8 pl-6 sm:pl-10 space-y-5">
          {careerSteps.map((step, idx) => {
            const isExpanded = expandedIndices.includes(idx);
            return (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, x: -15 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.07 }}
                className="relative group"
              >
                {/* Connector Dot */}
                <div className={`absolute -left-[31px] sm:-left-[47px] top-6 w-4 h-4 rounded-full border-2 transition-all duration-200 ${
                  idx === 0 
                    ? "bg-gradient-to-r from-indigo-500 to-cyan-400 border-white shadow-[0_0_15px_rgba(6,182,212,0.8)] scale-125" 
                    : "bg-[#0b1222] border-white/40 group-hover:border-cyan-400 group-hover:bg-cyan-500"
                }`} />

                {/* Timeline Card */}
                <div 
                  onClick={() => toggleIndex(idx)}
                  className={`p-4 sm:p-5 rounded-2xl transition-all duration-300 cursor-pointer border ${
                    isExpanded 
                      ? "bg-[#0d1527] border-cyan-400/40 shadow-xl shadow-black/60" 
                      : "bg-[#0a0f1d]/70 border-white/[0.08] hover:bg-[#0e162a] hover:border-cyan-500/30"
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white/[0.06] p-1 flex items-center justify-center border border-white/10 overflow-hidden flex-shrink-0">
                        <img 
                          src={step.logo} 
                          alt={step.company} 
                          className="max-h-full max-w-full object-contain filter brightness-95"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-base sm:text-lg text-white group-hover:text-cyan-300 transition-colors">
                          {step.role}
                        </h3>
                        <p className="text-xs sm:text-sm font-semibold text-white/80 flex items-center gap-2">
                          <span className="text-cyan-400 font-bold">{step.company}</span>
                          <span className="text-white/30 hidden sm:inline">•</span>
                          <span className="text-white/50 text-xs hidden sm:inline">{step.type}</span>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2.5 self-start sm:self-auto">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.05] border border-white/[0.08] text-xs font-mono text-white/70">
                        <Calendar className="w-3 h-3 text-indigo-400" />
                        {step.period}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleIndex(idx);
                        }}
                        aria-label={isExpanded ? "Hide key achievements" : "View key achievements"}
                        className={`w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-200 ${
                          isExpanded 
                            ? "bg-cyan-500/20 border-cyan-400/50 text-cyan-300 shadow-[0_0_12px_rgba(6,182,212,0.35)]" 
                            : "bg-white/[0.06] border-white/10 text-white/60 group-hover:text-cyan-300 group-hover:border-cyan-400/40 group-hover:bg-cyan-500/10"
                        }`}
                      >
                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? "rotate-180 text-cyan-300" : "rotate-0"}`} />
                      </button>
                    </div>
                  </div>

                  {/* Expandable Content: Below pointers and skill tags */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-white/[0.08] animate-fadeIn">
                      {/* Bullet Highlights */}
                      <div className="space-y-2.5">
                        {step.highlights.map((highlight, hIdx) => (
                          <div key={hIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-white/80 font-normal leading-relaxed">
                            <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                            <span>{highlight}</span>
                          </div>
                        ))}
                      </div>

                      {/* Skills tags */}
                      <div className="flex flex-wrap gap-1.5 mt-5 pt-3.5 border-t border-white/[0.06]">
                        {step.skills.map((skill, sIdx) => (
                          <span 
                            key={sIdx}
                            className="text-[11px] font-mono px-2.5 py-1 rounded-md bg-white/[0.04] text-white/65 border border-white/[0.05]"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}

          {/* Education Step */}
          <motion.div 
            initial={{ opacity: 0, x: -15 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.4 }}
            className="relative group"
          >
            <div className="absolute -left-[31px] sm:-left-[47px] top-4 w-4 h-4 rounded-full border-2 bg-[#121218] border-amber-400/80" />
            <div className="p-4 sm:p-5 rounded-2xl bg-[#101015]/70 border border-white/[0.06]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-400 flex items-center justify-center border border-amber-500/20 flex-shrink-0">
                  <GraduationCap className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-base sm:text-lg text-white">
                    B.Tech in Electrical & Electronics Engineering
                  </h3>
                  <p className="text-xs font-mono text-white/50 mt-0.5">
                    Graduated 2017 · Strong Foundation in Systems Engineering & Analytics
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}
